import React, { useState } from 'react'
import $t from 'i18n'
import { isEmpty } from 'lodash'
import Card from '../common/Card'
import Page from '../common/Page'
import MaterialInput from '../common/MaterialInput'
import validateZipcode, { ZIP_CODE_NOT_FOUND } from 'utils/validateZipcode'
import { gcpStorageUrl } from 'consts'
import { Button } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import Actions from 'actions'
import { event, eventTypes } from 'consts/eventConsts'
import ZipcodeValidationField from '../common/ZipcodeValidationField'
import routes from 'consts/routes'
import { STEPS } from 'consts/flowConsts'
import {
  getOwnerMember,
  getMemberCounty,
  getMemberEmail,
  getMemberZipCode
} from 'utils/memberUtils'
import { useLocation } from 'react-router'
import DatePicker from 'components/common/DatePicker'
import { isDateValid } from 'utils/validations'
import classNames from 'classnames'

import './Home.css'

const getValidationErrors = ({
  email,
  zipCode,
  memberAuthInfo,
  isBackFromSaveLink
}) => {
  const result = {}

  if (isBackFromSaveLink) {
    if (!isDateValid(memberAuthInfo?.dob)) {
      result.dob = $t('errors.validation.invalidDob')
    }
    if (!memberAuthInfo?.zipCode) {
      result.zipCode = $t('errors.validation.missing')
    } else if (!validateZipcode(memberAuthInfo?.zipCode)) {
      result.zipCode = $t('errors.validation.invalidZipcodeNumber')
    }
  } else {
    const EMAIL_REGEX =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

    if (!!email && !email?.toLowerCase().match(EMAIL_REGEX)) {
      result.email = $t('errors.validation.invalidEmail')
    }
    if (!zipCode) {
      result.zipCode = $t('errors.validation.missing')
    } else if (!validateZipcode(zipCode)) {
      result.zipCode = $t('errors.validation.invalidZipcode')
    }
  }

  return (!isEmpty(result) && result) || null
}

const Home = () => {
  const dispatch = useDispatch()
  const location = useLocation()

  const applicationId = location?.state?.applicationId
  const route = location?.state?.route

  const isBackFromSaveLink = applicationId && route

  const isLoading = useSelector(
    state => state.application.isSaveApplicationLoading
  )
  const zipCodeValidationStatus = useSelector(
    state => state.member?.zipCodeValidationStatus
  )

  const ownerMember = useSelector(state =>
    getOwnerMember(state.member?.members)
  )
  const appConfig = useSelector(state => state.app.appConfig)

  const zipCode = getMemberZipCode(ownerMember)
  const email = getMemberEmail(ownerMember)
  const selectedCounty = getMemberCounty(ownerMember)

  const [memberAuthInfo, setMemberAuthInfo] = useState({
    dob: null,
    zipCode: null
  })
  const [shouldValidate, setShouldValidate] = useState(false)
  const errors =
    shouldValidate &&
    getValidationErrors({ email, zipCode, memberAuthInfo, isBackFromSaveLink })

  const isButtonDisabled =
    !zipCode ||
    !!errors ||
    (!selectedCounty && zipCodeValidationStatus !== ZIP_CODE_NOT_FOUND)

  const isEnrollPortalDisabled = !appConfig?.portal_open

  const onProceed = () => {
    const validationErrors = getValidationErrors({ email, zipCode })
    const payload = { zipCode, county: selectedCounty, email }

    if (validationErrors) {
      setShouldValidate(true)
      dispatch(
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.formEvent,
          eventName: event.formEvent.invalid,
          data: payload,
          element: 'See plans and prices',
          userScope: {
            zipcode: zipCode,
            usa_state: validateZipcode(zipCode)
          }
        })
      )
      return
    }

    if (zipCodeValidationStatus !== ZIP_CODE_NOT_FOUND) {
      dispatch(
        Actions.createAction(Actions.STEP_COMPLETED, {
          step: STEPS.ZIP_CODE
        })
      )
    } else {
      dispatch(
        Actions.createAction(Actions.NAVIGATED_TO, routes.STATE_NOT_ALLOWED)
      )
    }
  }

  const onSubmitMemberAuthInfo = () => {
    if (getValidationErrors({ memberAuthInfo, isBackFromSaveLink })) {
      setShouldValidate(true)
      return
    }
    dispatch(
      Actions.createAction(Actions.WELCOME_BACK_REQUESTED, {
        memberAuthInfo,
        applicationId,
        route
      })
    )
    setShouldValidate(false)
  }

  const welcomeBackError = useSelector(
    state => state.application.welcomeBackError
  )

  const renderWelcomeBackContent = error => {
    const className = classNames('welcome-back-error', {
      visible: error
    })

    return (
      <>
        <div className='field dob'>
          <div className='field-title'>{$t('fields.dob')}</div>
          <DatePicker
            errorText={errors?.dob}
            defaultDob={memberAuthInfo?.dob}
            setSelectedDate={dob =>
              setMemberAuthInfo({ ...memberAuthInfo, dob })
            }
          />
        </div>
        <MaterialInput
          label={$t('fields.zip')}
          errorText={errors?.zipCode}
          value={memberAuthInfo?.zipCode}
          onChange={e => {
            setMemberAuthInfo({ ...memberAuthInfo, zipCode: e?.target?.value })
          }}
        />
        <Button
          className='submit-btn'
          type='primary'
          onClick={onSubmitMemberAuthInfo}
          loading={isLoading}
        >
          {$t('common.submit')}
        </Button>
        <div className={className}> {error} </div>
      </>
    )
  }

  const renderHomeContent = () => {
    return (
      <>
        <ZipcodeValidationField memberId={ownerMember?.id} errors={errors} />
        <MaterialInput
          className='contact-input'
          value={email}
          type='email'
          errorText={errors?.email}
          onChange={e => {
            dispatch(
              Actions.createAction(Actions.UPDATE_OWNER_MEMBER_EMAIL, {
                email: e.target.value
              })
            )
          }}
          label={$t('fields.emailOptional')}
        />
        <Button
          className='see-plans-btn'
          type='primary'
          onClick={onProceed}
          loading={isLoading}
          disabled={isButtonDisabled}
        >
          {$t('home.seePlans')}
        </Button>
        <div className='terms'>{$t('home.note')}</div>
      </>
    )
  }

  return (
    <Card
      className={
        isEnrollPortalDisabled ? 'home-card home-disabled' : 'home-card'
      }
    >
      <>
        <img
          className='mobile-header-img'
          alt=''
          src={`${gcpStorageUrl}/home_illustration-mobile.png`}
        />
        <Page
          title={
            isBackFromSaveLink ? $t('welcomeBack.title') : $t('home.title')
          }
          className='home-page'
          canNavigateBack={false}
        >
          {isEnrollPortalDisabled && (
            <div className='home-disabled-content'>
              {$t('home.notAvailable')}
            </div>
          )}
          {!isEnrollPortalDisabled && (
            <div className='home-content'>
              <div className='description'>
                {isBackFromSaveLink
                  ? $t('welcomeBack.description')
                  : $t('home.description')}
                .
              </div>
              {isBackFromSaveLink
                ? renderWelcomeBackContent(welcomeBackError)
                : renderHomeContent()}
            </div>
          )}
        </Page>
        <img
          className='home-img'
          alt=''
          src={`${gcpStorageUrl}/home_illustration.png`}
        />
      </>
    </Card>
  )
}

export default Home
